import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useNavigate } from 'react-router-dom';

const PlanFeature = ({ text }) => (
  <ListItem sx={{ py: 0.5 }}>
    <ListItemIcon sx={{ minWidth: 36 }}>
      <CheckCircleIcon color="primary" fontSize="small" />
    </ListItemIcon>
    <ListItemText primary={text} />
  </ListItem>
);

const PlanCard = ({ title, price, description, features, isPopular, onSelect }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Card 
      sx={{ 
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        border: isPopular ? `2px solid ${theme.palette.primary.main}` : 'none',
        transform: isPopular ? 'scale(1.05)' : 'none',
        zIndex: isPopular ? 1 : 0,
        '&:hover': {
          transform: isPopular ? 'scale(1.05)' : 'scale(1.02)',
          transition: 'transform 0.2s ease-in-out',
        }
      }}
    >
      {isPopular && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            bgcolor: 'primary.main',
            color: 'white',
            px: 2,
            py: 0.5,
            borderRadius: '0 0 8px 8px',
            fontSize: '0.875rem',
            fontWeight: 'bold',
            boxShadow: 1
          }}
        >
          Most Popular
        </Box>
      )}
      <CardContent sx={{ flexGrow: 1, pt: isPopular ? 3 : 2 }}>
        <Typography variant="h5" component="h2" gutterBottom>
          {title}
        </Typography>
        <Typography variant="h4" component="div" gutterBottom>
          {price}
          <Typography component="span" variant="body2" color="text.secondary">
            /month
          </Typography>
        </Typography>
        <Typography color="text.secondary" paragraph>
          {description}
        </Typography>
        <Divider sx={{ my: 2 }} />
        <List>
          {features.map((feature, index) => (
            <PlanFeature key={index} text={feature} />
          ))}
        </List>
      </CardContent>
      <Box sx={{ p: 2, pt: 0 }}>
        <Button
          variant={isPopular ? "contained" : "outlined"}
          fullWidth
          size="large"
          onClick={onSelect}
        >
          Get Started
        </Button>
      </Box>
    </Card>
  );
};

const Plan = () => {
  const navigate = useNavigate();

  const plans = [
    {
      title: "Free",
      price: "$0",
      description: "Try out evidence processing features",
      features: [
        "Video transcription (30 mins/month)",
        "Redaction (100MB/month)",
        "Document summarization (50 pages/month)",
        "Video conversion (500MB/month)",
        "Secure sharing (1GB/month)",
        "Standard processing speed",
        "Email support"
      ],
      isPopular: false
    },
    {
      title: "Plus",
      price: "$49",
      description: "Increased limits for active users",
      features: [
        "Video transcription (5 hours/month)",
        "Redaction (2GB/month)",
        "Document summarization (500 pages/month)",
        "Video conversion (5GB/month)",
        "Secure sharing (10GB/month)",
        "2x faster processing speed",
        "Priority email support"
      ],
      isPopular: true
    },
    {
      title: "Pro",
      price: "$199",
      description: "High-volume processing needs",
      features: [
        "Video transcription (20 hours/month)",
        "Redaction (10GB/month)",
        "Document summarization (2000 pages/month)",
        "Video conversion (20GB/month)",
        "Secure sharing (50GB/month)",
        "5x faster processing speed",
        "24/7 priority support",
        "Dedicated account manager"
      ],
      isPopular: false
    }
  ];

  return (
    <div className="page-container">
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography variant="h4" className="page-title" gutterBottom>
          Choose Your Plan
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
          Select the plan that best fits your digital evidence processing needs. All plans include our core features with additional capabilities as you scale.
        </Typography>
      </Box>

      <Grid container spacing={3} justifyContent="center">
        {plans.map((plan, index) => (
          <Grid item xs={12} md={4} key={index}>
            <PlanCard
              {...plan}
              onSelect={() => navigate('/settings')}
            />
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mt: 6, textAlign: 'center' }}>
        <Typography variant="h6" gutterBottom>
          Need a custom plan?
        </Typography>
        <Typography color="text.secondary" paragraph>
          For organizations with specific requirements or high-volume needs, we offer custom enterprise plans.
        </Typography>
        <Button variant="outlined" size="large">
          Contact Sales
        </Button>
      </Box>
    </div>
  );
};

export default Plan; 